import { useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2, UploadCloud, X } from "lucide-react";
import { api, apiError } from "@/lib/api";

const MAX_BYTES = 5 * 1024 * 1024;

export default function ProductMedia({ images, setImages }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const uploadFiles = async (fileList) => {
    const files = [...fileList];
    if (!files.length) return;
    for (const f of files) {
      if (!/\.(jpe?g|png|webp)$/i.test(f.name)) {
        toast.error(`${f.name}: use JPG, PNG or WEBP`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name} is over 5 MB`);
        continue;
      }
      setUploading((n) => n + 1);
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/uploads", fd);
        setImages((imgs) => [...imgs, data.url]);
      } catch (e) {
        toast.error(apiError(e, `Couldn't upload ${f.name}`));
      } finally {
        setUploading((n) => n - 1);
      }
    }
  };

  const move = (i, dir) =>
    setImages((imgs) => {
      const j = i + dir;
      if (j < 0 || j >= imgs.length) return imgs;
      const a = [...imgs];
      [a[i], a[j]] = [a[j], a[i]];
      return a;
    });

  const remove = (i) => setImages((imgs) => imgs.filter((_, x) => x !== i));

  return (
    <div data-testid="media-section">
      <div
        data-testid="media-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
          dragOver ? "border-amber-500/70 bg-amber-500/[0.06]" : "border-white/15 bg-black/30 hover:border-amber-500/40"
        }`}
      >
        <UploadCloud className={`h-8 w-8 ${dragOver ? "text-amber-400" : "text-neutral-500"}`} />
        <p className="text-sm font-semibold text-neutral-200">Drop images here or click to browse</p>
        <p className="text-xs text-neutral-500">JPG, PNG or WEBP · up to 5 MB each · first image is the primary</p>
        <input
          ref={inputRef}
          data-testid="media-upload-input"
          type="file"
          accept=".jpg,.jpeg,.png,.webp"
          multiple
          className="hidden"
          onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }}
        />
      </div>

      {(images.length > 0 || uploading > 0) && (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="media-grid">
          {images.map((url, i) => (
            <div key={url + i} className="group relative overflow-hidden rounded-xl border border-white/10 bg-black/40" data-testid={`media-item-${i}`}>
              <img src={url} alt={`Product ${i + 1}`} className="aspect-square w-full object-cover" />
              {i === 0 && (
                <span className="absolute left-2 top-2 rounded-md bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-black" data-testid="media-primary-badge">
                  PRIMARY
                </span>
              )}
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-black/70 p-1.5 opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
                <button type="button" data-testid={`media-move-left-${i}`} onClick={() => move(i, -1)} disabled={i === 0}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-neutral-300 hover:bg-white/10 disabled:opacity-30" aria-label="Move left">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button type="button" data-testid={`media-remove-${i}`} onClick={() => remove(i)}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-red-400 hover:bg-red-500/10" aria-label="Remove image">
                  <X className="h-4 w-4" />
                </button>
                <button type="button" data-testid={`media-move-right-${i}`} onClick={() => move(i, 1)} disabled={i === images.length - 1}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-neutral-300 hover:bg-white/10 disabled:opacity-30" aria-label="Move right">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
          {Array.from({ length: uploading }).map((_, i) => (
            <div key={`up-${i}`} className="flex aspect-square items-center justify-center rounded-xl border border-amber-500/30 bg-amber-500/[0.04]" data-testid={`media-uploading-${i}`}>
              <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
            </div>
          ))}
        </div>
      )}
      {images.length === 0 && uploading === 0 && (
        <p className="mt-3 text-xs text-neutral-600" data-testid="media-empty-hint">No images yet — a product needs at least one image before it can be saved.</p>
      )}
    </div>
  );
}
